import {
    Box,
    IconButton,
    Table as MaterialTable,
    TableBody,
    Grid,
    TableCell,
    TableHead,
    TableRow,
    Typography,
    withStyles
} from '@material-ui/core';
import PropTypes from 'prop-types';
import React, { useEffect, useState, useRef } from 'react';
import { AddBlue } from '../../../../../assets/icons';
import styles from './styles';
import NotFound from './../../../../common/NotFound';
import { v4 } from 'uuid';
import LaserTableRow from './LaserTableRow';
import Scrollbar from 'react-scrollbars-custom';

const headCells = [
    'Area',
    'Rx no',
    '% Imp',
    'Side',
    'Skin type',
    'Handpiece / tip / spot',
    'Fluence (Jcm2)',
    'PW (ms)',
    'Freq (Hz)',
    'Shots',
    'Cooling',
    'TP'
];

function Table({
    classes,
    formRef,
    disabled,
    ToUploadBliplTableRef,
    setComparisionBLIPLData,
    setIsCurrentlyEditingNote,
    hideOptions
}) {
    const [rows, setRows] = useState([]);

    const tableContentRef = useRef(null);
    const [scrollHeight, setScrollHeight] = useState(100);

    useEffect(() => {
        if (!tableContentRef.current) return;

        const updateHeight = () => {
            const h = tableContentRef.current.clientHeight + 20;
            setScrollHeight(h);
        };

        updateHeight();

        const ro = new ResizeObserver(updateHeight);
        ro.observe(tableContentRef.current);

        return () => ro.disconnect();
    }, [rows]);

    useEffect(() => {
        setRows(
            formRef.current.bliplRows || [
                {
                    testPatch: false,
                    cooling: 'No',
                    id: v4()
                }
            ]
        );
        //eslint-disable-next-line
    }, []);

    const updateFormRef = bliplRows => {
        setComparisionBLIPLData(true);
        formRef.current.bliplRows = bliplRows;
    };

    const addRow = () => {
        const bliplRows = [...rows, { testPatch: false, cooling: 'No', id: v4() }];
        setRows(bliplRows);
        updateFormRef(bliplRows);
    };

    const deleteRow = index => {
        const newArr = [...rows];
        setRows([]);
        newArr.splice(index, 1);
        setRows(newArr);
        updateFormRef(newArr);
        setIsCurrentlyEditingNote(true);
    };

    const copyRow = row => {
        const newRows = [...rows];
        if (row?.rx) {
            const allRxOptions = new Array(100).fill('').map((_, index) => index + 1);
            const currentlyUsedRx = rows.map(rowOption => rowOption?.rx);
            const biggestRxAvaliable = allRxOptions.filter(el => !currentlyUsedRx.includes(el));
            newRows.push({ ...row, rx: biggestRxAvaliable[0] });
        } else {
            newRows.push(row);
        }
        setRows(newRows);
        updateFormRef(newRows);
        setIsCurrentlyEditingNote(true);
    };

    const setValue = (e, index) => {
        const row = rows[index];
        row[e.target.name] = e.target.value;
        const newRows = [...rows];
        newRows.splice(index, 1, row);
        setRows(newRows);
        updateFormRef(newRows);
    };

    const setAutocompleteValue = (value, index, key) => {
        const row = rows[index];
        row[key] = value;
        const newRows = [...rows];
        newRows.splice(index, 1, row);
        setRows(newRows);
        updateFormRef(newRows);
        setIsCurrentlyEditingNote(true);
    };

    const renderTable = () => {
        const totalShots = `Total Shots ${rows.reduce((counter, row) => {
            if (isNaN(row.shots)) return counter;
            return Number(row.shots) + counter;
        }, 0)}`;

        return (
            <Box ref={ToUploadBliplTableRef} style={{ width: '100%' }}>
                {rows.length ? (
                    <Scrollbar
                        noScrollY
                        style={{ width: '100%', height: scrollHeight }}
                        wrapperProps={{ style: { width: '100%' } }}
                        contentProps={{ style: { display: 'inline-block' } }}
                    >
                        <div ref={tableContentRef} style={{ display: 'inline-block', verticalAlign: 'top' }}>
                            <MaterialTable size="small" stickyHeader className={`${classes.table} scrollbar-test`}>
                                <TableHead>
                                    <TableRow>
                                        {headCells.map(headCell => (
                                            <TableCell key={headCell} align="center" className={classes.tableHead}>
                                                {headCell}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>

                                <TableBody>
                                    {rows.map((row, index) => (
                                        <LaserTableRow
                                            key={`${row.id}-${index}`}
                                            id={row.id}
                                            classes={classes}
                                            setValue={setValue}
                                            setAutocompleteValue={setAutocompleteValue}
                                            setIsCurrentlyEditingNote={setIsCurrentlyEditingNote}
                                            deleteRow={() => deleteRow(index)}
                                            copyRow={() => copyRow({ ...row, id: v4() })}
                                            row={row}
                                            index={index}
                                            disabled={formRef.current.lockEdit || disabled}
                                            hideOptions={hideOptions}
                                        />
                                    ))}
                                </TableBody>

                                {!disabled && (
                                    <Box p={2}>
                                        <Grid container direction="row" justify="space-between">
                                            <Box display="flex" flexDirection="row" alignItems="center">
                                                <Typography variant="h5" className={classes.defaultFont}>
                                                    New row
                                                </Typography>
                                                <IconButton disabled={formRef.current.lockEdit} onClick={addRow}>
                                                    <AddBlue variant="admin" />
                                                </IconButton>
                                            </Box>
                                        </Grid>
                                    </Box>
                                )}

                                <Typography className={classes.totalShots}>{totalShots}</Typography>
                            </MaterialTable>
                        </div>
                    </Scrollbar>
                ) : (
                    <NotFound message="No rows added" />
                )}

                {rows.length === 0 && !disabled && (
                    <Box p={2}>
                        <Grid container direction="row" justify="space-between">
                            <Box display="flex" flexDirection="row" alignItems="center">
                                <Typography variant="h5" className={classes.defaultFont}>
                                    New row
                                </Typography>
                                <IconButton disabled={formRef.current.lockEdit} onClick={addRow}>
                                    <AddBlue variant="admin" />
                                </IconButton>
                            </Box>
                        </Grid>
                    </Box>
                )}
            </Box>
        );
    };

    return (
        <div className={classes.treatmentBlock} style={{ marginBottom: '20px' }}>
            {renderTable()}
        </div>
    );
}

Table.propTypes = {
    classes: PropTypes.object.isRequired,
    formRef: PropTypes.object.isRequired,
    disabled: PropTypes.bool,
    setComparisionBLIPLData: PropTypes.object,
    ToUploadBliplTableRef: PropTypes.object.isRequired,
    setIsCurrentlyEditingNote: PropTypes.func.isRequired,
    hideOptions: PropTypes.bool
};

export default withStyles(styles)(Table);
